/**
 * Block Interactions
 * Handles initialization and interaction logic for custom quiz blocks
 */

(function() {
	'use strict';

	// Confetti library loading state
	let confettiLoading = false;
	let confettiLoaded = false;

	/**
	 * Load confetti library dynamically if scratch or spin wheel blocks exist
	 * @param {jQuery} screen - The screen element to check for blocks
	 */
	function loadConfettiIfNeeded(screen) {
		// Check if confetti is already loaded or loading
		if (confettiLoaded || confettiLoading) {
			return Promise.resolve();
		}

		// Check if scratch blocks or spin wheel blocks exist on this screen
		const hasScratchBlocks = $(screen).find('[data-scratch-discount]').length > 0;
		const hasSpinWheelBlocks = $(screen).find('[data-spin-wheel]').length > 0;
		if (!hasScratchBlocks && !hasSpinWheelBlocks) {
			return Promise.resolve();
		}

		// Check if script is already in DOM
		if (document.querySelector('script[src*="canvas-confetti"]')) {
			confettiLoaded = true;
			return Promise.resolve();
		}

		// Load confetti library
		confettiLoading = true;
		return new Promise(function(resolve, reject) {
			const script = document.createElement('script');
			script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.js';
			script.async = true;
			script.onload = function() {
				confettiLoaded = true;
				confettiLoading = false;
				resolve();
			};
			script.onerror = function() {
				confettiLoading = false;
				console.warn('Failed to load confetti library');
				reject(new Error('Failed to load confetti library'));
			};
			document.head.appendChild(script);
		});
	}

	/**
	 * Initialize spin wheel blocks
	 * @param {jQuery} screen - The screen element containing spin wheel blocks
	 * @param {Object} context - Context object with required dependencies
	 * @param {Object} context.screensManager - ScreensManager instance
	 */
	function initSpinWheel(screen, context) {
		const { screensManager } = context;

		// Load confetti library if spin wheel blocks exist
		loadConfettiIfNeeded(screen);

		$(screen).find('[data-spin-wheel]').each(function() {
			const $wheel = $(this);
			const wheelId = $wheel.attr('id');
			const $button = $(screen).find(`[data-spin-wheel-button="${wheelId}"]`);
			const onEnd = $wheel.attr('data-on-end') || 'next-screen';
			const hideButtonOnSpin = $button.attr('data-hide-on-spin') === 'true';
			let isSpinning = false;

			// Calculate rotation for selected segment
			function calculateRotation(selectedSegment) {
				const segmentRotations = {
					1: 330,     // Segment 1 (center 30deg) -> rotate 330deg clockwise (360-30)
					2: 270,     // Segment 2 (center 90deg) -> rotate 270deg clockwise (360-90)
					3: 210,     // Segment 3 (center 150deg) -> rotate 210deg clockwise (360-150)
					4: 150,     // Segment 4 (center 210deg) -> rotate 150deg clockwise (360-210)
					5: 90,      // Segment 5 (center 270deg) -> rotate 90deg clockwise (360-270)
					6: 30       // Segment 6 (center 330deg) -> rotate 30deg clockwise (360-330)
				};

				const baseRotationDeg = segmentRotations[selectedSegment] || 30;
				// Add random offset between -10 and +10 degrees to avoid exact center
				// This ensures we never stop exactly at 60-degree intervals
				const randomOffset = (Math.random() * 20) - 10; // -10 to +10 degrees
				const baseRotation = baseRotationDeg + randomOffset;

				// Add 5-8 full rotations (1800-2880 degrees) for realistic spin
				const fullRotations = Math.max(3, Math.round(Math.random() * 10)); // Random between 5-8 spins
				const extraRotation = fullRotations * 360;

				return baseRotation + extraRotation;
			}

			$button.on('click', function(e) {
				e.preventDefault();

				if (isSpinning) return;

				isSpinning = true;
				$button.prop('disabled', true);

				// Hide button if configured
				if (hideButtonOnSpin) {
					$button.hide();
				}

				const selectedSegment = parseInt($wheel.attr('data-selected-segment') || '1', 10);
				const rotation = calculateRotation(selectedSegment);

				// Reset rotation first (for multiple spins)
				$wheel.css('transition', 'none');
				$wheel.css('transform', 'rotate(0deg)');

				// Force reflow
				$wheel[0].offsetHeight;

				// Apply spin animation
				$wheel.css('transition', 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)');
				$wheel.css('transform', `rotate(${rotation}deg)`);

				// Handle end of animation
				setTimeout(function() {
					isSpinning = false;

					// Normalize rotation to 0-360 range for next spin
					const normalizedRotation = ((rotation % 360) + 360) % 360;
					$wheel.css('transition', 'none');
					$wheel.css('transform', `rotate(${normalizedRotation}deg)`);

					// Get the winning segment text
					const segmentText = $wheel.attr(`data-segment-${selectedSegment}`) || '';

					w2w.logEvent('Spin Wheel Completed', {
						segment: selectedSegment,
						prize: segmentText
					});

					// Trigger confetti animation
					if (typeof confetti !== 'undefined') {
						// Get wheel position for confetti origin
						const rect = $wheel[0].getBoundingClientRect();
						const x = (rect.left + rect.width / 2) / window.innerWidth;
						const y = (rect.top + rect.height / 2) / window.innerHeight;

						confetti({
							particleCount: 100,
							spread: 70,
							origin: { x: x, y: y },
							colors: ['#FFD700', '#FFA500', '#FF6347', '#FF1493', '#00CED1', '#32CD32']
						});
					}

					// Handle on_end behavior
					if (onEnd === 'next-screen') {
						// Delay redirect by 2 seconds to show confetti
						setTimeout(function() {
							if (!getParams('edit_mode')) {
								screensManager.moveToNextScreen();
							} else {
								w2w.showToast('Edit mode: Move to next screen disabled');
							}
						}, 2000);
					} else if (onEnd === 'undisable') {
						// Undisable the Next button
						const $nextButton = $(screen).find('[data-screen-next]');
						if ($nextButton.length) {
							$nextButton.removeAttr('disabled');
						}
						// Re-enable spin button if not hidden
						if (!hideButtonOnSpin) {
							$button.prop('disabled', false);
						}
					} else if (onEnd === 'nothing') {
						// Do nothing - button stays hidden if hideButtonOnSpin is true
						if (!hideButtonOnSpin) {
							$button.prop('disabled', false);
						}
					} else {
						// Default: re-enable button if not hidden
						if (!hideButtonOnSpin) {
							$button.prop('disabled', false);
						}
					}
				}, 4000);
			});
		});
	}

	/**
	 * Initialize scratch discount blocks
	 * @param {jQuery} screen - The screen element containing scratch discount blocks
	 * @param {Object} context - Context object with required dependencies
	 * @param {Object} context.screensManager - ScreensManager instance
	 */
	function initScratchDiscount(screen, context) {
		const { screensManager } = context;

		// Load confetti library if scratch blocks exist
		loadConfettiIfNeeded(screen);

		$(screen).find('[data-scratch-discount]').each(function() {
			const canvas = this;
			const $canvas = $(canvas);
			const $container = $canvas.closest('.type-scratch-discount');
			const $wrapper = $canvas.closest('.type-scratch-discount__wrapper');
			const ctx = canvas.getContext('2d');
			const onEnd = $canvas.attr('data-on-end') || 'next-screen';
			const scratchImageUrl = $canvas.attr('data-scratch-image') || '';
			const scratchColor = $canvas.attr('data-scratch-color') || '#FFD700';

			// If configured to "undisable" on end, Next must start disabled.
			if (onEnd === 'undisable') {
				$(screen).find('[data-screen-next]').attr('disabled', 'disabled');
			}

			let isScratching = false;
			let lastX = 0;
			let lastY = 0;
			let scratchedPixels = 0;
			let totalPixels = 0;
			const scratchThreshold = 0.7; // share of promo text area scratched to trigger completion
			let scratchImage = null;
			let imageLoaded = false;

			// Load scratch surface image
			if (scratchImageUrl) {
				scratchImage = new Image();
				scratchImage.crossOrigin = 'anonymous';
				scratchImage.onload = function() {
					imageLoaded = true;
					initCanvas();
				};
				scratchImage.onerror = function() {
					console.warn('Failed to load scratch image:', scratchImageUrl);
					imageLoaded = false;
					initCanvas(); // Fallback to color
				};
				scratchImage.src = scratchImageUrl;
			} else {
				imageLoaded = false;
			}

			// Get canvas dimensions
			function initCanvas() {
				const width = $wrapper.width() || 300;
				const height = parseFloat(getComputedStyle($canvas[0]).getPropertyValue('--type-scratch-discount-height')) || 300;

				// Set canvas size
				canvas.width = width;
				canvas.height = height;

				// Calculate total pixels
				totalPixels = width * height;

				// Draw scratch surface - use image if available, otherwise use color
				if (imageLoaded && scratchImage) {
					// Draw the image to fill the canvas
					ctx.drawImage(scratchImage, 0, 0, width, height);
				} else {
					// Fallback to solid color
					ctx.fillStyle = scratchColor;
					ctx.fillRect(0, 0, canvas.width, canvas.height);

					// Add texture/pattern to make it look more like a scratch card
					ctx.globalCompositeOperation = 'multiply';
					for (let i = 0; i < 100; i++) {
						const x = Math.random() * width;
						const y = Math.random() * height;
						const size = Math.random() * 3 + 1;
						ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.1})`;
						ctx.fillRect(x, y, size, size);
					}
					ctx.globalCompositeOperation = 'source-over';
				}

				// Show reveal content now that canvas is ready (it will be covered by canvas)
				const $revealContent = $container.find('.type-scratch-discount__reveal-content');
				$revealContent.addClass('visible');

				// Reset scratched pixels
				scratchedPixels = 0;
			}

			// Initialize canvas (will be called again when image loads if image is provided)
			if (!scratchImageUrl || imageLoaded) {
				initCanvas();
			}

			// Handle window resize
			let resizeTimeout;
			const handleResize = function() {
				clearTimeout(resizeTimeout);
				resizeTimeout = setTimeout(function() {
					// Re-initialize canvas with current dimensions
					initCanvas();
				}, 100);
			};
			$(window).on('resize', handleResize);

			// Clean up on screen change
			$canvas.on('remove', function() {
				$(window).off('resize', handleResize);
			});

			// Get event position relative to canvas
			function getEventPos(e) {
				const rect = canvas.getBoundingClientRect();
				const scaleX = canvas.width / rect.width;
				const scaleY = canvas.height / rect.height;

				if (e.touches && e.touches.length > 0) {
					return {
						x: (e.touches[0].clientX - rect.left) * scaleX,
						y: (e.touches[0].clientY - rect.top) * scaleY
					};
				}
				return {
					x: (e.clientX - rect.left) * scaleX,
					y: (e.clientY - rect.top) * scaleY
				};
			}

			// Check if scratched enough
			function checkScratchProgress() {
				// Count only the promo text area (whole canvas if there is no text)
				const canvasRect = canvas.getBoundingClientRect();
				const textRect = $container.find('.type-scratch-discount__text')[0].getBoundingClientRect();
				const textX = Math.max(0, Math.floor((textRect.left - canvasRect.left) * canvas.width / canvasRect.width));
				const textY = Math.max(0, Math.floor((textRect.top - canvasRect.top) * canvas.height / canvasRect.height));
				const textW = Math.min(canvas.width, Math.ceil((textRect.right - canvasRect.left) * canvas.width / canvasRect.width)) - textX;
				const textH = Math.min(canvas.height, Math.ceil((textRect.bottom - canvasRect.top) * canvas.height / canvasRect.height)) - textY;

				// Get image data to count transparent pixels
				const imageData = textW > 0 && textH > 0
					? ctx.getImageData(textX, textY, textW, textH)
					: ctx.getImageData(0, 0, canvas.width, canvas.height);
				const data = imageData.data;
				let transparentPixels = 0;

				// Sample every 10th pixel for performance
				for (let i = 0; i < data.length; i += 40) {
					if (data[i + 3] === 0) { // Alpha channel is 0 (transparent)
						transparentPixels++;
					}
				}

				// Estimate total scratched pixels
				const scratchedRatio = transparentPixels / (data.length / 40);

				if (scratchedRatio >= scratchThreshold && !$canvas.data('completed')) {
					$canvas.data('completed', true);
					handleScratchComplete();
				}
			}

			// Handle scratch completion
			function handleScratchComplete() {
				// Log event
				w2w.logEvent('Scratch Discount Completed', {
					revealText: $container.find('.type-scratch-discount__text').text()
				});

				// Trigger confetti animation
				if (typeof confetti !== 'undefined') {
					// Get canvas position for confetti origin
					const rect = canvas.getBoundingClientRect();
					const x = (rect.left + rect.width / 2) / window.innerWidth;
					const y = (rect.top + rect.height / 2) / window.innerHeight;

					confetti({
						particleCount: 100,
						spread: 70,
						origin: { x: x, y: y },
						colors: ['#FFD700', '#FFA500', '#FF6347', '#FF1493', '#00CED1', '#32CD32']
					});
				}

				// Handle on_end behavior
				if (onEnd === 'next-screen') {
					// Delay redirect by 2 seconds to show confetti
					setTimeout(function() {
						if (!getParams('edit_mode')) {
							screensManager.moveToNextScreen();
						} else {
							w2w.showToast('Edit mode: Move to next screen disabled');
						}
					}, 2000);
				} else if (onEnd === 'undisable') {
					// Undisable the Next button
					const $nextButton = $(screen).find('[data-screen-next]');
					if ($nextButton.length) {
						$nextButton.removeAttr('disabled');
					}
				} else if (onEnd === 'nothing') {
					// Do nothing
				}
			}

			// Start scratching
			function startScratching(e) {
				e.preventDefault();
				isScratching = true;
				const pos = getEventPos(e);
				lastX = pos.x;
				lastY = pos.y;
			}

			// Scratch (erase)
			function scratch(e) {
				if (!isScratching) return;
				e.preventDefault();

				const pos = getEventPos(e);

				// Use destination-out to erase (reveal content underneath)
				ctx.globalCompositeOperation = 'destination-out';
				ctx.lineWidth = 50; // Scratch brush size
				ctx.lineCap = 'round';
				ctx.lineJoin = 'round';
				ctx.beginPath();
				ctx.moveTo(lastX, lastY);
				ctx.lineTo(pos.x, pos.y);
				ctx.stroke();

				lastX = pos.x;
				lastY = pos.y;

				// Check progress
				checkScratchProgress();
			}

			// Stop scratching
			function stopScratching(e) {
				if (isScratching) {
					e.preventDefault();
					isScratching = false;
				}
			}

			// Mouse events
			canvas.addEventListener('mousedown', startScratching);
			canvas.addEventListener('mousemove', scratch);
			canvas.addEventListener('mouseup', stopScratching);
			canvas.addEventListener('mouseleave', stopScratching);

			// Touch events
			canvas.addEventListener('touchstart', startScratching);
			canvas.addEventListener('touchmove', scratch);
			canvas.addEventListener('touchend', stopScratching);
			canvas.addEventListener('touchcancel', stopScratching);
		});
	}

	/**
	 * Initialize signature blocks
	 * @param {jQuery} screen - The screen element containing signature blocks
	 * @param {Object} context - Context object with required dependencies
	 * @param {Object} context.screensManager - ScreensManager instance with validateAllRequirements method
	 */
	function initSignature(screen, context) {
		const { screensManager } = context;

		$(screen).find('[data-signature]').each(function() {
			const canvas = this;
			const $canvas = $(canvas);
			const $container = $canvas.closest('.type-signature');
			const ctx = canvas.getContext('2d');

			// Get values from CSS variables
			const computedStyle = window.getComputedStyle($canvas[0]);
			const penColor = computedStyle.getPropertyValue('--type-signature-pen-color').trim() ||
				getComputedStyle(document.documentElement).getPropertyValue('--type-signature-pen-color').trim() || '#000000';
			const penWidthStr = computedStyle.getPropertyValue('--type-signature-pen-width').trim() ||
				getComputedStyle(document.documentElement).getPropertyValue('--type-signature-pen-width').trim() || '2px';
			const penWidth = parseFloat(penWidthStr) || 2;
			const backgroundColor = computedStyle.getPropertyValue('--type-signature-canvas-background').trim() ||
				getComputedStyle(document.documentElement).getPropertyValue('--type-signature-canvas-background').trim() || '#ffffff';

			// Get canvas dimensions from CSS
			const canvasWidth = computedStyle.getPropertyValue('--type-signature-canvas-width').trim() ||
				getComputedStyle(document.documentElement).getPropertyValue('--type-signature-canvas-width').trim() || '100%';
			const canvasHeight = computedStyle.getPropertyValue('--type-signature-canvas-height').trim() ||
				getComputedStyle(document.documentElement).getPropertyValue('--type-signature-canvas-height').trim() || '200px';

			// Function to get actual pixel dimensions
			function getPixelValue(value, isWidth) {
				if (value.includes('%')) {
					// For percentage, calculate based on container
					const container = $canvas.parent();
					const containerSize = isWidth ? container.width() : container.height();
					return Math.floor((parseFloat(value) / 100) * containerSize);
				} else {
					// For fixed values (px, em, etc.), parse the number
					return parseFloat(value) || (isWidth ? 400 : 200);
				}
			}

			// Set canvas CSS dimensions first (for percentage width)
			$canvas.css({
				width: canvasWidth,
				height: canvasHeight
			});

			// Get actual pixel dimensions after CSS is applied
			const width = $canvas.width() || getPixelValue(canvasWidth, true);
			const height = $canvas.height() || getPixelValue(canvasHeight, false);

			// Set canvas internal dimensions (for drawing)
			canvas.width = width;
			canvas.height = height;

			const fieldName = $canvas.attr('data-name');
			const isRequired = $canvas.attr('data-required') === 'true';
			const $hiddenInput = $container.find('[data-signature-value]').first();

			// Set canvas background
			ctx.fillStyle = backgroundColor;
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			let isDrawing = false;
			let lastX = 0;
			let lastY = 0;

			function getEventPos(e) {
				const rect = canvas.getBoundingClientRect();
				const scaleX = canvas.width / rect.width;
				const scaleY = canvas.height / rect.height;

				if (e.touches && e.touches.length > 0) {
					return {
						x: (e.touches[0].clientX - rect.left) * scaleX,
						y: (e.touches[0].clientY - rect.top) * scaleY
					};
				}
				return {
					x: (e.clientX - rect.left) * scaleX,
					y: (e.clientY - rect.top) * scaleY
				};
			}

			function startDrawing(e) {
				e.preventDefault();
				isDrawing = true;
				const pos = getEventPos(e);
				lastX = pos.x;
				lastY = pos.y;
			}

			function draw(e) {
				if (!isDrawing) return;
				e.preventDefault();

				const pos = getEventPos(e);

				ctx.beginPath();
				ctx.moveTo(lastX, lastY);
				ctx.lineTo(pos.x, pos.y);
				ctx.strokeStyle = penColor;
				ctx.lineWidth = penWidth;
				ctx.lineCap = 'round';
				ctx.lineJoin = 'round';
				ctx.stroke();

				lastX = pos.x;
				lastY = pos.y;

				// Update hidden input with base64 data
				updateSignatureValue();

				// Validate if required
				if (isRequired) {
					screensManager.validateAllRequirements(screen);
				}
			}

			function stopDrawing(e) {
				if (isDrawing) {
					e.preventDefault();
					isDrawing = false;
				}
			}

			function updateSignatureValue() {
				const dataURL = canvas.toDataURL('image/png');
				$hiddenInput.val(dataURL ? 1 : '');

				// // Update analytics if signature has content
				// if (fieldName && dataURL && dataURL.length > 100) {
				// 	//AnalyticsManager.setUserProperty(fieldName, dataURL);
				// }
			}

			function clearSignature() {
				// Re-read background color from CSS variable in case it changed
				const currentBgColor = computedStyle.getPropertyValue('--type-signature-canvas-background').trim() ||
					getComputedStyle(document.documentElement).getPropertyValue('--type-signature-canvas-background').trim() || '#ffffff';
				ctx.fillStyle = currentBgColor;
				ctx.fillRect(0, 0, canvas.width, canvas.height);
				$hiddenInput.val('');

				if (isRequired) {
					screensManager.validateAllRequirements(screen);
				}
			}

			// Mouse events
			canvas.addEventListener('mousedown', startDrawing);
			canvas.addEventListener('mousemove', draw);
			canvas.addEventListener('mouseup', stopDrawing);
			canvas.addEventListener('mouseleave', stopDrawing);

			// Touch events
			canvas.addEventListener('touchstart', startDrawing);
			canvas.addEventListener('touchmove', draw);
			canvas.addEventListener('touchend', stopDrawing);
			canvas.addEventListener('touchcancel', stopDrawing);

			// Clear button
			$canvas.siblings('[data-signature-clear]').on('click', function(e) {
				e.preventDefault();
				clearSignature();
			});

			// Handle window resize for percentage-based width
			if (canvasWidth.includes('%')) {
				let resizeTimeout;
				const handleResize = function() {
					clearTimeout(resizeTimeout);
					resizeTimeout = setTimeout(function() {
						const newWidth = $canvas.width();
						const newHeight = $canvas.height();

						// Only resize if dimensions actually changed
						if (newWidth !== canvas.width || newHeight !== canvas.height) {
							// Save current drawing as image
							const currentImage = new Image();
							currentImage.src = canvas.toDataURL('image/png');

							// Resize canvas
							const oldWidth = canvas.width;
							const oldHeight = canvas.height;
							canvas.width = newWidth;
							canvas.height = newHeight;

							// Restore background
							ctx.fillStyle = backgroundColor;
							ctx.fillRect(0, 0, canvas.width, canvas.height);

							// Restore drawing scaled to new size
							currentImage.onload = function() {
								ctx.drawImage(currentImage, 0, 0, oldWidth, oldHeight, 0, 0, newWidth, newHeight);
								updateSignatureValue();
							};
						}
					}, 100);
				};

				$(window).on('resize', handleResize);

				// Clean up on screen change
				$canvas.on('remove', function() {
					$(window).off('resize', handleResize);
				});
			}
		});
	}


	////////////////////////
	function initLiking(){
		var CONFIG = {
			thresholds: {
				throwDistance: 40,
				velocityBoost: 400,
				minSwipeTime: 30,
				clickMaxDistance: 5,
				clickMaxTime: 200
			},
			timing: {
				animationDuration: 400,
				resetDuration: 300,
				navigationDelay: 500
			}
		};
		var deck = document.querySelector('.container-type-liking');
		if (!deck) {
			return;
		}
		var card = deck.querySelector('.type-liking-swiper-card');
		var interactionOverlay = document.querySelector('.type-liking-swiper-interaction-overlay');


		window.els_index = $(".type-liking-img").length - 1

		var state = {
			isDragging: false,
			startX: 0,
			startY: 0,
			currentX: 0,
			currentY: 0,
			startTime: 0,
			endTime: 0,
			velocityHistory: [],
			currentDistance: 0,
			maxDistance: 0,
			wasSwipe: false
		};

		function updateVisuals(distance) {
			$(".type-liking-img").removeClass("type-liking-animation");
			clearInterval(window.tinder1); clearInterval(window.tinder2);
			var absDistance = Math.abs(distance);
			var progress = Math.min(absDistance / CONFIG.thresholds.throwDistance, 1);
			var rotation = (distance / deck.offsetWidth) * 30;
			var card = $('.type-liking-swiper-card')[window.els_index];
			card.style.transform = 'translateX(' + distance + 'px) rotate(' + rotation + 'deg)';
			if (distance<0) {
				$(card).find(".type-liking-text-left").css("display", "block");
				$(card).find(".type-liking-text-right").hide()
			} else {
				$(card).find(".type-liking-text-right").css("display", "block");
				$(card).find(".type-liking-text-left").hide()
			}

			state.currentDistance = distance;

			if (Math.abs(distance) > state.maxDistance) {
				state.maxDistance = Math.abs(distance);
			}
		}

		function calculateVelocity() {
			if (state.velocityHistory.length < 2) return 0;

			var recent = state.velocityHistory.slice(-3);
			var totalDistance = 0;
			var totalTime = 0;

			for (var i = 1; i < recent.length; i++) {
				totalDistance += Math.abs(recent[i].x - recent[i-1].x);
				totalTime += recent[i].time - recent[i-1].time;
			}

			return totalTime > 0 ? totalDistance / totalTime : 0;
		}

		function shouldThrow(distance, velocity) {
			var absDistance = Math.abs(distance);
			return absDistance > CONFIG.thresholds.throwDistance ||
				(velocity > CONFIG.thresholds.velocityBoost && absDistance > 20);
		}

		function isClick() {
			var totalTime = state.endTime - state.startTime;
			return state.maxDistance <= CONFIG.thresholds.clickMaxDistance &&
				totalTime <= CONFIG.thresholds.clickMaxTime;
		}

		function throwCard(direction) {
			var throwDistance = direction === 'left' ? -deck.offsetWidth * 1.2 : deck.offsetWidth * 1.2;
			var rotation = direction === 'left' ? -30 : 30;
			var card = $('.type-liking-swiper-card')[window.els_index];
			card.style.transition = 'transform ' + CONFIG.timing.animationDuration + 'ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
			card.style.transform = 'translateX(' + throwDistance + 'px) rotate(' + rotation + 'deg)';
			var name = $(".quiz__screen").attr("id")+"_"+window.els_index;
			if (direction === 'left') AnalyticsManager.setUserProperty(name, "0"); else
				AnalyticsManager.setUserProperty(name, "1");
			window.els_index--;
			if (window.els_index < 0){
				if (direction === 'left') $(".type-liking-text-bar-left").click(); else $(".type-liking-text-bar-right").click();
			}
			setTimeout(function() {
				card.style.display = 'none';

			}, CONFIG.timing.animationDuration);
		}

		function resetCard() {
			var card = $('.type-liking-swiper-card')[window.els_index];
			card.style.transition = 'transform ' + CONFIG.timing.resetDuration + 'ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
			card.style.transform = 'translateX(0px) rotate(0deg)';
			$(card).find(".type-liking-text-left").css("display", "none")
			$(card).find(".type-liking-text-right").css("display", "none")



			setTimeout(function() {
				card.style.transition = '';
				state.currentDistance = 0;
			}, CONFIG.timing.resetDuration);
		}

		function handleStart(e) {
			if (state.isDragging) return;

			state.isDragging = true;
			state.startX = e.clientX || e.touches[0].clientX;
			state.startY = e.clientY || e.touches[0].clientY;
			state.currentX = state.startX;
			state.currentY = state.startY;
			state.startTime = Date.now();
			state.currentDistance = 0;
			state.maxDistance = 0;
			state.wasSwipe = false;
			state.velocityHistory = [{
				x: state.startX,
				time: state.startTime
			}];

			card.style.transition = '';
			interactionOverlay.style.cursor = 'grabbing';
		}

		function handleMove(e) {
			if (!state.isDragging) return;

			e.preventDefault();

			var newX = e.clientX || e.touches[0].clientX;
			var newY = e.clientY || e.touches[0].clientY;

			state.currentX = newX;
			state.currentY = newY;

			var deltaX = newX - state.startX;
			var currentTime = Date.now();

			if (Math.abs(deltaX) > CONFIG.thresholds.clickMaxDistance) {
				state.wasSwipe = true;
			}

			state.velocityHistory.push({
				x: newX,
				time: currentTime
			});

			if (state.velocityHistory.length > 5) {
				state.velocityHistory.shift();
			}

			updateVisuals(deltaX);
		}

		function handleEnd(e) {
			if (!state.isDragging) return;

			state.isDragging = false;
			state.endTime = Date.now();
			interactionOverlay.style.cursor = 'grab';

			var deltaX = state.currentX - state.startX;
			var velocity = calculateVelocity();
			var direction = deltaX > 0 ? 'right' : 'left';

			if (shouldThrow(deltaX, velocity)) {
				throwCard(direction);
			} else {
				resetCard();
			}
		}

		function handleClick(direction) {
			if (state.wasSwipe || !isClick()) {
				return;
			}

			throwCard(direction);
		}

		// Обработчики событий для оверлея
		interactionOverlay.addEventListener('pointerdown', handleStart);
		document.addEventListener('pointermove', handleMove);
		document.addEventListener('pointerup', handleEnd);

		interactionOverlay.addEventListener('touchstart', handleStart, { passive: false });
		document.addEventListener('touchmove', handleMove, { passive: false });
		document.addEventListener('touchend', handleEnd);


	}
	/**
	 * Initialize toggle blocks
	 * @param {jQuery} screen - The screen element containing toggle blocks
	 * @param {Object} context - Context object with required dependencies (optional)
	 */
		function initToggle(screen, context) {
		// Flag to prevent recursive calls during synchronization
		let isSynchronizing = false;

		// Handler function for toggle changes
		function handleToggleChange() {
			// Skip if we're already synchronizing to prevent infinite loops
			if (isSynchronizing) {
				return;
			}

			const $toggle = $(this);
			const checked = $toggle.is(':checked');
			const name = $toggle.attr('name');

			// Synchronize all toggles with the same name
			if (name) {
				isSynchronizing = true;
				$('.toggle-input[name="' + name + '"]').each(function() {
					const $otherToggle = $(this);
					// Only update if not already in the correct state and not the current toggle
					if ($otherToggle.is(':checked') !== checked && !$otherToggle.is($toggle[0])) {
						$otherToggle.prop('checked', checked);
					}
				});
				isSynchronizing = false;
			}

			// Set user property and log analytics
			if (name) {
				w2w.setUserProperty(name, checked ? '1' : '');
				w2w.logEvent("Toggle " + name, {
					value: checked ? '1' : ''
				});
			}

			// Execute callback if provided
			if ($toggle.attr('data-callback')) {
				const rawData = $toggle.attr('data-callback') || '';
				const jsonString = rawData.replace(/&quot;/g, '"');
				try {
					const data = JSON.parse(jsonString);
					const callback = data.callback;

					if (callback) {
						try {
							if (typeof evaluateFunction === 'function') {
								evaluateFunction(callback, {checked: checked, name: name});
							} else {
								// Fallback: try to eval if evaluateFunction is not available
								const func = new Function('return ' + callback)();
								if (typeof func === 'function') {
									func({checked: checked, name: name});
								}
							}
						} catch (e) {
							console.error('Error executing TOGGLE callback', e);
						}
					}
				} catch (e) {
					console.error('Error parsing TOGGLE callback data', e);
				}
			}
		}

		// Use off() with namespace to prevent duplicate handlers
		$(screen).find('.toggle-input').off('change.toggle').on("change.toggle", handleToggleChange);
	}

	function initPromoTicketCodes(screen) {
		const now = new Date();
		const $root = screen ? $(screen) : $(document);

		function getPromoLocale(preferBrowser = false) {
			const browserLocales = [navigator.language, navigator.userLanguage];
			const appLocales = [
				window.user_properties?.user_selected_language,
				window.quiz?.language,
				window.paywall?.language,
				window.locales?.language
			];
			const ordered = preferBrowser ? [...browserLocales, ...appLocales] : [...appLocales, ...browserLocales];
			return ordered.find(Boolean) || 'en';
		}

		function normalizePromoLocale(locale) {
			const value = String(locale || '').trim().replace(/_/g, '-');
			if (!value) {
				return 'en';
			}

			const parts = value.split('-');
			const lang = (parts[0] || '').toLowerCase();
			const hasExplicitScript = parts.slice(1).some(part => /^[A-Za-z]{4}$/.test(part));

			// Locales where browser/runtime may default to Cyrillic if script isn't explicit.
			// For promo codes we prefer predictable Latin output when script is ambiguous.
			const latinPreferredIfScriptMissing = new Set(['sr', 'bs', 'uz', 'az', 'kk', 'ky', 'mn', 'tg']);
			if (latinPreferredIfScriptMissing.has(lang) && !hasExplicitScript) {
				const region = parts.slice(1).find(part => /^[A-Za-z]{2}$/.test(part) || /^\d{3}$/.test(part));
				return region ? `${lang}-Latn-${region}` : `${lang}-Latn`;
			}

			return value;
		}

		function formatMonthIntl(locale, monthStyle) {
			try {
				return new Intl.DateTimeFormat(locale, { month: monthStyle }).format(now);
			} catch (e) {
				return new Intl.DateTimeFormat('en', { month: monthStyle }).format(now);
			}
		}

		function formatYearIntl(locale, yearStyle) {
			try {
				return new Intl.DateTimeFormat(locale, { year: yearStyle }).format(now);
			} catch (e) {
				return new Intl.DateTimeFormat('en', { year: yearStyle }).format(now);
			}
		}

		function normalizeToken(value, replaceSpacesWithUnderscore) {
			const spaceReplacement = replaceSpacesWithUnderscore ? '_' : '';
			return String(value || '')
				.toLowerCase()
				.replace(/\./g, '')
				.trim()
				.replace(/\s+/g, spaceReplacement);
		}

		function buildSuffix(mode, shouldTranslate) {
			const englishMonths = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
			const locale = normalizePromoLocale(shouldTranslate ? getPromoLocale(true) : getPromoLocale(false));

			if (mode === 'false' || mode === 'off' || mode === '') {
				return '';
			}

			if (mode === 'full') {
				const monthRaw = shouldTranslate ? formatMonthIntl(locale, 'long') : now.toLocaleString('en', { month: 'long' });
				const yearRaw = shouldTranslate ? formatYearIntl(locale, 'numeric') : String(now.getFullYear());
				const month = normalizeToken(monthRaw, true);
				const year = normalizeToken(yearRaw, false);
				return month + '_' + year;
			}

			// Backward compatible "true" (legacy) and explicit short mode.
			const monthRaw = shouldTranslate ? formatMonthIntl(locale, 'short') : englishMonths[now.getMonth()];
			const yearRaw = shouldTranslate ? formatYearIntl(locale, '2-digit') : String(now.getFullYear()).slice(-2);
			const month = normalizeToken(monthRaw, false);
			const year = normalizeToken(yearRaw, false);
			return month + year;
		}

		$root.find('.type-promo-ticket').each(function() {
			const $ticket = $(this);
			const promoBase = ($ticket.attr('data-promo-base') || '').trim();
			const suffixMode = ($ticket.attr('data-month-suffix') || 'true').trim().toLowerCase();
			const translateSuffix = ($ticket.attr('data-month-suffix-translate') || 'false').trim().toLowerCase() === 'true';
			const customSuffix = ($ticket.attr('data-code-suffix') || '').trim();
			const autoSuffix = buildSuffix(suffixMode, translateSuffix);
			const finalSuffix = customSuffix || autoSuffix;
			const promoCode = finalSuffix ? (promoBase ? promoBase + '_' + finalSuffix : finalSuffix) : promoBase;

			$ticket.find('.type-promo-ticket__code-text').text(promoCode);
		});
	}

	// Scroll position saved before locking body (iOS Safari needs explicit restore)
	let fullscreenVideoScrollY = null;

	/**
	 * Reset fullscreen video layout and scroll lock (call before leaving a video screen)
	 */
	function resetFullscreenVideo() {
		const $body = $('body');
		const $html = $('html');
		const hadScrollLock = $body.hasClass('fullscreen-video-active');
		const scrollY = fullscreenVideoScrollY !== null ? fullscreenVideoScrollY : window.scrollY;

		$('.quiz__screen.has-fullscreen-video').each(function() {
			const $screen = $(this);

			$screen.find('.video-fullscreen-adaptable').each(function() {
				const blockIndex = $(this).attr('data-block-area-index') || 'default';
				$(window).off('resize.fullscreen-video-' + blockIndex);
			});

			$screen.find('.video-fullscreen-adaptable').css({
				'position': '',
				'top': '',
				'left': '',
				'width': '',
				'height': '',
				'padding': '',
				'margin': ''
			});

			$screen.css({
				'position': '',
				'top': '',
				'left': '',
				'width': '',
				'height': '',
				'min-height': '',
				'padding': '',
				'overflow': '',
				'z-index': ''
			});

			$screen.off('screen-exit.fullscreen-video');
			$screen.removeClass('has-fullscreen-video');
		});

		$body.removeClass('fullscreen-video-active');
		$html.removeClass('fullscreen-video-active');
		$body.css({
			'overflow': '',
			'position': '',
			'top': '',
			'width': '',
			'height': ''
		});
		$html.css('overflow', '');
		fullscreenVideoScrollY = null;

		if (hadScrollLock) {
			requestAnimationFrame(function() {
				window.scrollTo(0, scrollY);
			});
		}
	}

	/**
	 * Initialize fullscreen adaptable video blocks
	 * Modifies parent containers to allow videos to fill the viewport
	 * @param {jQuery} screen - The screen element containing video blocks
	 */
	function initFullscreenVideo(screen) {
		if ($(screen).find('.video-fullscreen-adaptable').length === 0) {
			return;
		}

		$(screen).find('.video-fullscreen-adaptable').each(function() {
			const $videoContainer = $(this);
			const $screen = $videoContainer.closest('.quiz__screen');
			const $body = $('body');
			const $html = $('html');

			if ($screen.length) {
				$screen.addClass('has-fullscreen-video');
			}

			function setFullscreenStyles() {
				const viewportHeight = window.innerHeight;

				if ($screen.length) {
					$screen.css({
						'position': 'fixed',
						'top': '0',
						'left': '0',
						'width': '100%',
						'height': viewportHeight + 'px',
						'min-height': viewportHeight + 'px',
						'padding': '0',
						'overflow': 'hidden',
						'z-index': '1'
					});
				}

				$videoContainer.css({
					'position': 'absolute',
					'top': '0',
					'left': '0',
					'width': '100%',
					'height': '100%',
					'padding': '0',
					'margin': '0'
				});

				if (fullscreenVideoScrollY === null) {
					fullscreenVideoScrollY = window.scrollY;
				}

				$html.addClass('fullscreen-video-active');
				$body.addClass('fullscreen-video-active');
			}

			setFullscreenStyles();

			function handleResize() {
				setFullscreenStyles();
			}

			const resizeNamespace = 'resize.fullscreen-video-' + ($videoContainer.attr('data-block-area-index') || 'default');
			$(window).off(resizeNamespace).on(resizeNamespace, handleResize);

			$screen.on('screen-exit.fullscreen-video', function() {
				resetFullscreenVideo();
			});
		});
	}

	/**
	 * Initialize video controls (play/pause, timing, progress, next button)
	 * @param {jQuery} screen - The screen element containing video blocks
	 * @param {Object} context - Context object with required dependencies
	 * @param {Object} context.screensManager - ScreensManager instance
	 */
	// Store video instances for cleanup
	const videoInstances = new Map();

	function initVideoControls(screen, context) {
		const screensManager = context?.screensManager || window.screensManager;

		$(screen).find('video[data-video-index]').each(function() {
			const $video = $(this);
			const videoIndex = $video.attr('data-video-index');
			const $controls = $(screen).find(`[data-video-controls-index="${videoIndex}"]`);
			const $playPauseIcon = $(screen).find(`[data-video-icon-index="${videoIndex}"]`);
			const video = $video[0];

			// Store instance data for cleanup
			const instanceData = {
				video: video,
				$video: $video,
				intervals: [],
				timeouts: [],
				eventHandlers: [],
				$screen: $(screen)
			};
			videoInstances.set(videoIndex, instanceData);

			// Show play/pause icon feedback
			function showPlayPauseIcon(isPlaying) {
				if ($playPauseIcon.length) {
					$playPauseIcon.removeClass('playing paused').addClass(isPlaying ? 'playing' : 'paused');
					$playPauseIcon.addClass('show');

					setTimeout(function() {
						$playPauseIcon.removeClass('show');
					}, 1000); // Hide after 1 second
				}
			}

			// Click on video to play/pause (only if enabled)
			const clickToPlayPause = $video.attr('data-click-to-play-pause');
			if (clickToPlayPause === 'true' || clickToPlayPause === true) {
				$video.off('click.video-controls').on('click.video-controls', function(e) {
					e.preventDefault();
					e.stopPropagation();

					if (video.paused) {
						video.play().catch(function(err) {
							console.warn('Video play failed:', err);
						});
					} else {
						video.pause();
					}
				});
			}

			// Hide controls after delay (declare before handlers)
			let hideControlsTimeout;
			let controlsInteractionTimeout;

			// Show controls
			function showControls() {
				if ($controls.length) {
					$controls.removeClass('hidden');
				}
			}

			// Hide controls
			function hideControls() {
				if ($controls.length) {
					$controls.addClass('hidden');
				}
			}

			// Update controls visibility based on video state
			function updateControlsVisibility() {
				if ($controls.length) {
					if (video.paused) {
						// Always show controls when paused
						showControls();
						clearTimeout(hideControlsTimeout);
					} else {
						// Hide controls immediately when playing
						hideControls();
						clearTimeout(hideControlsTimeout);
					}
				}
			}

			// Update play/pause button state (declare early)
			function updatePlayPauseButton() {
				const $playPauseButton = $controls.find('.type-video__controls-play-pause');
				if ($playPauseButton.length) {
					$playPauseButton.removeClass('playing paused').addClass(video.paused ? 'paused' : 'playing');
				}
			}

			// Mute/unmute button update (declare early)
			function updateMuteButton() {
				const $muteButton = $controls.find('.type-video__controls-mute');
				if ($muteButton.length) {
					$muteButton.toggleClass('muted', video.muted);
				}
			}

			// Video event listeners for play/pause icon and controls
			const playHandler = function() {
				showPlayPauseIcon(true);
				updatePlayPauseButton();
				updateControlsVisibility();
			};

			const pauseHandler = function() {
				showPlayPauseIcon(false);
				updatePlayPauseButton();
				updateControlsVisibility(); // Always show when paused
			};

			// Store handlers for cleanup
			instanceData.eventHandlers.push(
				{ element: video, event: 'play', handler: playHandler },
				{ element: video, event: 'pause', handler: pauseHandler },
				{ element: video, event: 'volumechange', handler: updateMuteButton }
			);

			video.addEventListener('play', playHandler);
			video.addEventListener('pause', pauseHandler);
			video.addEventListener('volumechange', updateMuteButton);

			// Initial state
			updatePlayPauseButton();
			updateMuteButton();

			// Track views: send "Video Progress" every N seconds of actual watch time
			if ($video.attr('data-track-views') === 'true') {
				const isEditMode = new URLSearchParams(window.location.search).get('edit_mode') === '1';
				const interval = parseFloat($video.attr('data-track-views-interval')) > 0
					? parseFloat($video.attr('data-track-views-interval'))
					: 5;
				let watchedSeconds = 0;
				let lastTime = null;
				let nextMark = interval;

				const sendProgress = function() {
					if (isEditMode) return;
					const duration = isFinite(video.duration) ? video.duration : 0;
					const position = video.currentTime || 0;
					w2w.logEvent('Video Progress', {
						quiz_id: window.quiz_id || AnalyticsManager.quiz_id || 0,
						screen_id: $video.attr('data-screen-id') || '',
						video_index: parseInt(videoIndex, 10),
						video_src: $video.attr('data-video-src') || '',
						watched_seconds: Math.round(watchedSeconds),
						position: Math.round(position * 10) / 10,
						duration: Math.round(duration * 10) / 10,
						percent: duration > 0 ? Math.round((position / duration) * 100) : 0
					});
				};

				const trackTimeUpdate = function() {
					if (video.paused && !video.ended) {
						lastTime = null;
						return;
					}
					const now = video.currentTime || 0;
					const delta = lastTime === null ? 0 : now - lastTime;
					// Ignore seeks/loops: only count natural forward playback
					if (delta > 0 && delta < 2) {
						watchedSeconds += delta;
					}
					lastTime = now;
					while (watchedSeconds >= nextMark) {
						sendProgress();
						nextMark += interval;
					}
				};
				const trackReset = function() { lastTime = null; };

				instanceData.eventHandlers.push(
					{ element: video, event: 'timeupdate', handler: trackTimeUpdate },
					{ element: video, event: 'pause', handler: trackReset },
					{ element: video, event: 'seeking', handler: trackReset }
				);
				video.addEventListener('timeupdate', trackTimeUpdate);
				video.addEventListener('pause', trackReset);
				video.addEventListener('seeking', trackReset);
			}

			if ($controls.length === 0) {
				return; // No controls for this video, but play/pause icon still works
			}

			const $timeCurrent = $controls.find('.type-video__controls-time-current');
			const $timeTotal = $controls.find('.type-video__controls-time-total');
			const $progressBar = $controls.find('.type-video__controls-progress-bar');
			const $progressContainer = $controls.find('.type-video__controls-progress');
			const $nextButton = $controls.find('.type-video__controls-next');
			const $playPauseButton = $controls.find('.type-video__controls-play-pause');
			const $muteButton = $controls.find('.type-video__controls-mute');

			// Format time as MM:SS
			function formatTime(seconds) {
				if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
				const mins = Math.floor(seconds / 60);
				const secs = Math.floor(seconds % 60);
				return mins + ':' + String(secs).padStart(2, '0');
			}

			// Update time display and progress bar
			function updateTime() {
				const current = video.currentTime || 0;
				const duration = video.duration || 0;

				if ($timeCurrent.length) {
					$timeCurrent.text(formatTime(current));
				}
				if ($timeTotal.length && duration > 0) {
					$timeTotal.text(formatTime(duration));
				}

				if ($progressBar.length && duration > 0) {
					const percent = (current / duration) * 100;
					$progressBar.css('width', percent + '%');
				}
			}

			// Click on play/pause button
			$playPauseButton.on('click', function(e) {
				e.preventDefault();
				e.stopPropagation();

				if (video.paused) {
					video.play().catch(function(err) {
						console.warn('Video play failed:', err);
					});
				} else {
					video.pause();
				}
			});

			// Click on progress bar to seek
			$progressContainer.on('click', function(e) {
				e.stopPropagation();
				if (video.duration) {
					const rect = this.getBoundingClientRect();
					const percent = (e.clientX - rect.left) / rect.width;
					video.currentTime = percent * video.duration;
					updateTime();
				}
			});

			// Mute/unmute functionality
			$muteButton.on('click', function(e) {
				e.preventDefault();
				e.stopPropagation();
				video.muted = !video.muted;
				updateMuteButton();
			});

			// Play Next button
			$nextButton.on('click', function(e) {
				e.preventDefault();
				e.stopPropagation();

				// Pause video
				video.pause();

				screensManager.moveToNextScreen();
			});

			// Time update handlers
			const loadedMetadataHandler = function() { updateTime(); };
			const loadedDataHandler = function() { updateTime(); };
			const timeUpdateHandler = function() { updateTime(); };
			const progressHandler = function() { updateTime(); };
			const endedHandler = function() {
				updateControlsVisibility();

				// Handle on_video_end action
				const onVideoEnd = $video.attr('data-on-video-end');
				if (onVideoEnd === 'move_to_next_screen') {
					// Check if in edit mode
					const urlParams = new URLSearchParams(window.location.search);
					const isEditMode = urlParams.get('edit_mode') === '1';

					if (!isEditMode) {
						screensManager.moveToNextScreen();
					} else {
						w2w.showToast('Edit mode: Move to next screen disabled');
					}
				}
			};

			// Store handlers for cleanup
			instanceData.eventHandlers.push(
				{ element: video, event: 'loadedmetadata', handler: loadedMetadataHandler },
				{ element: video, event: 'loadeddata', handler: loadedDataHandler },
				{ element: video, event: 'timeupdate', handler: timeUpdateHandler },
				{ element: video, event: 'progress', handler: progressHandler },
				{ element: video, event: 'ended', handler: endedHandler }
			);

			// Video event listeners for time updates
			video.addEventListener('loadedmetadata', loadedMetadataHandler);
			video.addEventListener('loadeddata', loadedDataHandler);
			video.addEventListener('timeupdate', timeUpdateHandler);
			video.addEventListener('progress', progressHandler);
			video.addEventListener('ended', endedHandler);

			// Show controls on touch/mouse move (for when playing)
			const interactionHandler = function() {
				if (!video.paused) {
					// Show controls temporarily when interacting while playing
					showControls();
					clearTimeout(controlsInteractionTimeout);
					controlsInteractionTimeout = setTimeout(function() {
						if (!video.paused) {
							hideControls();
						}
					}, 3000);
				}
			};

			$(screen).on('mousemove.fullscreen-video-' + videoIndex + ' touchstart.fullscreen-video-' + videoIndex, interactionHandler);
			instanceData.$screen = $(screen);
			instanceData.interactionNamespace = 'mousemove.fullscreen-video-' + videoIndex + ' touchstart.fullscreen-video-' + videoIndex;

			// Initial state: show controls if paused
			updateControlsVisibility();

			// Start time updates immediately and set up interval as fallback
			updateTime();

			// Fallback: update time every 100ms if timeupdate event doesn't fire
			const timeUpdateInterval = setInterval(function() {
				if (video.readyState >= 2) {
					updateTime();
				}
			}, 100);

			// Autoplay video on page load
			// Try to play video if it's paused and ready
			const tryAutoplay = function() {
				if (video.paused && video.readyState >= 2) { // HAVE_CURRENT_DATA or better
					// Videos usually need to be muted for autoplay to work in browsers
					const wasMuted = video.muted;
					if (!wasMuted) {
						video.muted = true;
					}

					video.play().then(function() {
						// Autoplay succeeded - restore original muted state if changed
						if (!wasMuted) {
							video.muted = wasMuted;
						}
					}).catch(function(err) {
						// Autoplay failed (likely due to browser restrictions)
						// Restore original muted state
						if (!wasMuted) {
							video.muted = wasMuted;
						}
						// Silently fail - user can click to play
					});
				}
			};

			// Try to autoplay when video is ready
			if (video.readyState >= 2) {
				// Video is ready, try to play after a short delay
				setTimeout(tryAutoplay, 100);
			} else {
				// Wait for video to load enough data
				video.addEventListener('loadeddata', tryAutoplay, { once: true });
			}

			// Store interval and timeout for cleanup
			instanceData.intervals.push(timeUpdateInterval);
			if (hideControlsTimeout) {
				instanceData.timeouts.push(hideControlsTimeout);
			}
			if (controlsInteractionTimeout) {
				instanceData.timeouts.push(controlsInteractionTimeout);
			}
			instanceData.intervals.push(timeUpdateInterval);
			instanceData.timeouts.push(hideControlsTimeout);
			if (typeof controlsInteractionTimeout !== 'undefined') {
				instanceData.timeouts.push(controlsInteractionTimeout);
			}

			// Store event handlers for cleanup
			instanceData.eventHandlers.push({
				element: video,
				event: 'play',
				handler: playHandler
			});
			instanceData.eventHandlers.push({
				element: video,
				event: 'pause',
				handler: pauseHandler
			});
			instanceData.eventHandlers.push({
				element: video,
				event: 'loadedmetadata',
				handler: function() { updateTime(); }
			});
			instanceData.eventHandlers.push({
				element: video,
				event: 'loadeddata',
				handler: function() { updateTime(); }
			});
			instanceData.eventHandlers.push({
				element: video,
				event: 'timeupdate',
				handler: function() { updateTime(); }
			});
			instanceData.eventHandlers.push({
				element: video,
				event: 'progress',
				handler: function() { updateTime(); }
			});
			instanceData.eventHandlers.push({
				element: video,
				event: 'ended',
				handler: function() { updateControlsVisibility(); }
			});
			instanceData.eventHandlers.push({
				element: video,
				event: 'volumechange',
				handler: updateMuteButton
			});
		});
	}

	/**
	 * Cleanup video controls when quiz is updated
	 * Removes all event listeners, intervals, and timeouts for videos
	 */
	function cleanupVideoControls() {
		// Cleanup all stored video instances
		videoInstances.forEach(function(instanceData, videoIndex) {
			// Clear intervals
			instanceData.intervals.forEach(function(intervalId) {
				if (intervalId) {
					clearInterval(intervalId);
				}
			});

			// Clear timeouts
			instanceData.timeouts.forEach(function(timeoutId) {
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
			});

			// Remove event listeners
			instanceData.eventHandlers.forEach(function(handlerData) {
				if (handlerData.element && handlerData.handler) {
					handlerData.element.removeEventListener(handlerData.event, handlerData.handler);
				}
			});

			// Remove jQuery event listeners
			if (instanceData.$video) {
				instanceData.$video.off('click.video-controls');
			}

			// Remove screen interaction listeners
			if (instanceData.$screen && instanceData.interactionNamespace) {
				instanceData.$screen.off(instanceData.interactionNamespace);
			}
		});

		// Clear the map
		videoInstances.clear();

		// Remove all video-related event listeners from screens (fallback cleanup)
		$('.quiz__screen').off('mousemove.fullscreen-video touchstart.fullscreen-video');

		resetFullscreenVideo();
	}

	// ---------------------------------------------------------------------------
	// App Store Page: view model for app-store-page.ejs. The template only prints
	// what this returns, so every default, fallback and visibility rule lives here.
	// ---------------------------------------------------------------------------

	const APP_STORE_CATEGORIES = {
		education: { label: 'Education', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features116/v4/83/50/c4/8350c46e-ff22-a9af-1b3b-f85f54073593/6ff3dcf1-f625-4318-8e1b-bdd42045114f.png/96x96bb.webp' },
		'social-networking': { label: 'Social Networking', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/e5/77/19/e57719b9-42de-af25-7ccd-9be8a082ddcf/7b2c7800-354d-45f4-8f9f-150e0d690ae2.png/96x96bb.webp' },
		games: { label: 'Games', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/3b/4f/be/3b4fbe61-aa0c-b1f2-f00b-670daff9e65a/dc339d5c-ba89-478a-a0fc-3107c08dd6f5.png/96x96bb.webp' },
		business: { label: 'Business', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features116/v4/6b/26/61/6b266131-b03c-10b8-5640-24ed75a1b527/9020ae77-3d4e-48fc-a546-8895dffe962b.png/96x96bb.webp' },
		productivity: { label: 'Productivity', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features116/v4/6e/e2/e7/6ee2e7db-bf98-0d30-7adf-15aa80debeb5/721e011e-109f-4227-8978-1f8c6071c48f.png/96x96bb.webp' },
		entertainment: { label: 'Entertainment', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/fb/1d/8d/fb1d8d13-8d9b-4667-4632-979fcce14060/7842b9eb-ef81-48c7-82c1-fee36005ca00.png/96x96bb.webp' },
		utilities: { label: 'Utilities', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/91/e4/61/91e461e4-ec59-0d3b-43e4-e7cdf77da4ff/0717b7dc-93c8-42da-9886-e0de0c534f2a.png/96x96bb.webp' },
		finance: { label: 'Finance', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/95/7a/d3/957ad33b-ed27-6d93-8ebc-5a7faa577e1d/7c133563-3209-4594-8bf7-7f1abe8fc8a6.png/96x96bb.webp' },
		'health-fitness': { label: 'Health & Fitness', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/ba/91/1a/ba911ac0-dae3-7dfc-46a5-3b2c1020955f/731619d1-37fa-4b57-ab3f-5621709a2343.png/96x96bb.webp' },
		'photo-video': { label: 'Photo & Video', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features116/v4/ec/8c/b5/ec8cb547-a88f-bd50-0bae-ecf33835cf8e/89d66422-d75a-40f6-abb4-7baafc06e593.png/96x96bb.webp' },
		music: { label: 'Music', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/14/5d/a1/145da182-f482-11c3-610b-a8c80cd73351/bd46b3db-b3c6-45c3-90fd-8a117ada9a5e.png/96x96bb.webp' },
		shopping: { label: 'Shopping', icon: 'https://is1-ssl.mzstatic.com/image/thumb/Features126/v4/9a/c6/3d/9ac63d45-ae18-9c56-aa72-5d89b93078c6/b15aea41-12af-42cf-b366-c528fa35e36b.png/96x96bb.webp' }
	};

	const APP_STORE_PRIVACY_ICONS = {
		tracking: '<svg viewBox="0 0 28 28" aria-hidden="true"><circle cx="14" cy="14" r="10.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M9.5 18.5l9-9M12 9.5h6.5V16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
		linked: '<svg viewBox="0 0 28 28" aria-hidden="true"><circle cx="14" cy="10" r="4" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M6 24c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
		'not-linked': '<svg viewBox="0 0 28 28" aria-hidden="true"><circle cx="14" cy="10" r="4" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M6 24c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M4 4l20 20" stroke="currentColor" stroke-width="1.5"/></svg>'
	};

	// Section toggles come from checkboxes and older JSON as booleans, 'true'/'false', 1/0 or 'hide'
	function appStoreIsShown(value, defaultShown) {
		if (value === undefined || value === null) {
			return defaultShown;
		}

		return value === true || value === 'true' || value === 1 || value === '1';
	}

	// Empty fields fall back to defaults; only the explicit '' of a cleared field is kept where noted
	function appStoreText(value, fallback) {
		return value === undefined || value === null || value === '' ? (fallback || '') : value;
	}

	// Five star slots for a 0..5 rating; a fractional star carries its fill percentage
	function appStoreStars(rating) {
		const value = Math.max(0, Math.min(5, parseFloat(rating) || 0));
		const stars = [];

		for (let i = 0; i < 5; i++) {
			const fill = Math.max(0, Math.min(1, value - i));

			stars.push({
				state: fill <= 0 ? 'empty' : (fill >= 1 ? 'filled' : 'partial'),
				percent: Math.round(fill * 100)
			});
		}

		return stars;
	}

	function appStoreReviews(item) {
		// Blocks saved before review_cards existed keep a single review in flat featured_review_* fields
		const reviews = Array.isArray(item.review_cards)
			? item.review_cards
			: [{
				title: item.featured_review_title,
				date: item.featured_review_date,
				stars: item.featured_review_stars,
				username: item.featured_review_username,
				content: item.featured_review_content
			}];

		return reviews
			.filter(review => review && (review.title || review.content))
			.map(review => ({
				title: review.title || '',
				date: review.date || '',
				username: review.username || '',
				content: review.content || '',
				stars: appStoreStars(review.stars || 5)
			}));
	}

	function appStorePageView(item) {
		item = item || {};

		const categoryKey = APP_STORE_CATEGORIES[item.category_type] ? item.category_type : 'business';
		const category = APP_STORE_CATEGORIES[categoryKey];
		const rating = appStoreText(item.rating_value, '4.7');
		const getButtonText = appStoreText(item.get_button_text, 'GET');
		const inAppPurchasesText = appStoreText(item.in_app_purchases_text, 'In-App Purchases');
		const percents = [item.star_5_percent, item.star_4_percent, item.star_3_percent, item.star_2_percent, item.star_1_percent];
		// A cleared Chart position swaps the CHART column for CATEGORY with its icon
		const showChart = item.chart_value !== '';

		return {
			iconUrl: item.app_icon_image || item.app_icon || '',
			name: item.app_name || '',
			subtitle: item.app_subtitle || '',
			getButtonText: getButtonText,
			inAppPurchasesText: inAppPurchasesText,

			metadata: {
				ratingLabel: appStoreText(item.rating_count_label, '178 RATINGS'),
				rating: rating,
				ratingStars: appStoreStars(rating),
				age: appStoreText(item.age_rating, '4+'),
				ageSubtitle: item.age_rating_subtitle || '',
				showChart: showChart,
				chart: appStoreText(item.chart_value, 'No. 7'),
				chartCategory: appStoreText(item.chart_category_label, category.label),
				categoryLabel: category.label,
				categoryIconUrl: category.icon,
				developer: appStoreText(item.developer_short_name, 'Pathfinder'),
				languageCode: appStoreText(item.language_code, 'EN'),
				languageName: appStoreText(item.language_name, 'English'),
				size: appStoreText(item.app_size_value, '77.6'),
				sizeUnit: appStoreText(item.app_size_unit, 'MB')
			},

			whatsNew: {
				show: appStoreIsShown(item.show_whats_new_section, true),
				title: appStoreText(item.whats_new_title, "What's New"),
				version: item.whats_new_version || '',
				date: item.whats_new_date || '',
				text: item.whats_new_text || ''
			},

			preview: {
				title: appStoreText(item.preview_section_title, 'Preview'),
				screenshots: (item.screenshot_cards || item.options || [])
					.filter(card => card && card.image)
					.map(card => card.image)
			},

			description: {
				text: item.description || '',
				moreText: item.description_more_text || '',
				developerName: item.developer_name || '',
				developerLabel: appStoreText(item.developer_row_label, 'Developer')
			},

			ratings: {
				title: appStoreText(item.ratings_section_title, 'Ratings & Reviews'),
				seeAllText: item.ratings_see_all_text || '',
				rating: rating,
				outOfText: appStoreText(item.rating_out_of_text, 'out of 5'),
				totalLabel: appStoreText(item.ratings_total_label, '151 Ratings'),
				// Bars from 5 to 1 stars: the row shows that many stars, the track fills to the given %
				bars: percents.map((percent, index) => ({
					stars: 5 - index,
					starsText: '★'.repeat(5 - index),
					percent: Math.max(0, Math.min(100, parseInt(percent, 10) || 0))
				})),
				reviews: appStoreReviews(item)
			},

			privacy: {
				show: appStoreIsShown(item.show_privacy_section, true),
				title: appStoreText(item.privacy_section_title, 'App Privacy'),
				seeDetailsText: item.privacy_see_details_text || '',
				description: item.privacy_description || '',
				cards: (item.privacy_cards || []).map(card => ({
					iconSvg: APP_STORE_PRIVACY_ICONS[card.icon_type] || APP_STORE_PRIVACY_ICONS['not-linked'],
					title: card.title || '',
					description: card.description || '',
					items: String(card.data_items || '').split(',').map(label => label.trim()).filter(Boolean)
				})),
				learnMoreText: item.privacy_learn_more_text || ''
			},

			information: {
				title: appStoreText(item.information_section_title, 'Information'),
				rows: (item.information_rows || []).filter(row => row && (row.label || row.value))
			},

			footerPrivacyText: item.footer_privacy_text || ''
		};
	}

	function getAppStoreScrollRoot(block) {
		const scrollParent = getScrollParent(block);

		if (scrollParent && scrollParent instanceof Element) {
			return scrollParent;
		}

		return null;
	}

	function bindAppStoreStickyHeader($block, $sticky) {
		const $header = $block.find('.type-app-store-page__header');

		if (!$header.length || typeof IntersectionObserver === 'undefined') {
			return null;
		}

		const scrollRoot = getAppStoreScrollRoot($block[0]);
		const observer = new IntersectionObserver(function(entries) {
			const entry = entries[0];

			if (!entry) {
				return;
			}

			const showSticky = !entry.isIntersecting;

			$sticky.toggleClass('is-visible', showSticky);
			$sticky.attr('aria-hidden', showSticky ? 'false' : 'true');
		}, {
			root: scrollRoot,
			threshold: 0,
			rootMargin: '0px'
		});

		observer.observe($header[0]);
		return observer;
	}

	function getScrollParent(element) {
		let parent = element ? element.parentElement : null;

		while (parent && parent !== document.body && parent !== document.documentElement) {
			const style = window.getComputedStyle(parent);
			const overflowY = style.overflowY;
			const overflow = style.overflow;

			if (/(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflow)) {
				return parent;
			}

			parent = parent.parentElement;
		}

		return null;
	}

	function shouldPortalAppStoreSticky() {
		const ua = navigator.userAgent || '';
		const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

		if (!isIOS) {
			return false;
		}

		if (/Telegram/i.test(ua)) {
			return true;
		}

		if (/Instagram|CriOS|FxiOS|EdgiOS/i.test(ua)) {
			return false;
		}

		return /Safari/i.test(ua);
	}

	function portalAppStoreSticky($sticky, $block) {
		if (!$sticky.length || $sticky.data('appStorePortaled') || !shouldPortalAppStoreSticky()) {
			return;
		}

		$sticky.data('appStorePortaled', true);
		$sticky.data('appStorePortalBlock', $block);
		$sticky.addClass('is-portaled');
		$('body').append($sticky);
		bindAppStoreStickyActions($sticky, $block);
	}

	function bindAppStoreStickyActions($sticky, $block) {
		$sticky.find('[data-screen-next]').off('.appStoreSticky').on('click.appStoreSticky', function(e) {
			if (!$sticky.hasClass('is-portaled')) {
				return;
			}

			e.preventDefault();
			e.stopPropagation();

			const mainButton = $block.find('.type-app-store-page__header [data-screen-next]').get(0);

			if (mainButton) {
				mainButton.click();
			}
		});
	}

	function restorePortaledAppStoreStickies() {
		$('body > [data-app-store-page-sticky]').each(function() {
			const $sticky = $(this);
			const $block = $sticky.data('appStorePortalBlock');

			$sticky.removeClass('is-visible is-portaled').attr('aria-hidden', 'true');

			if ($block && $block.length && $.contains(document, $block[0])) {
				$sticky.prependTo($block);
			} else {
				$sticky.remove();
			}

			$sticky.removeData('appStorePortaled appStorePortalBlock');
			$sticky.find('[data-screen-next]').off('.appStoreSticky');
		});
	}

	function getAppStoreSliderTrack(viewport) {
		return viewport.querySelector(':scope > .type-app-store-page__screenshots-track, :scope > .type-app-store-page__metadata-track, :scope > .type-app-store-page__reviews-track');
	}

	function bindAppStoreSliders($block) {
		$block.find('[data-app-store-slider]').each(function() {
			const viewport = this;
			const track = getAppStoreSliderTrack(viewport);

			if (!track || viewport.dataset.appStoreSliderBound === '1') {
				return;
			}

			viewport.dataset.appStoreSliderBound = '1';

			const state = {
				offset: 0,
				pointerId: null,
				startX: 0,
				startY: 0,
				offsetStart: 0,
				axis: null,
				lastX: 0,
				lastTime: 0,
				velocity: 0,
				dragged: false,
				wheelTimer: null
			};

			// The viewport bleeds to the screen edges with a left inset; mirror that inset on the right
			// so the last card stops at the same distance from the edge instead of being clipped.
			const getMaxOffset = function() {
				const inset = parseFloat(window.getComputedStyle(viewport).paddingLeft) || 0;

				return Math.max(0, track.scrollWidth - (viewport.clientWidth - inset * 2));
			};

			const applyOffset = function(value) {
				const maxOffset = getMaxOffset();

				state.offset = Math.max(0, Math.min(maxOffset, value));
				track.style.transform = state.offset ? 'translateX(' + (-state.offset) + 'px)' : '';
			};

			// Snap to the closest card start (like the native App Store carousel)
			const snapTo = function(target) {
				const maxOffset = getMaxOffset();
				let best = maxOffset;
				let bestDistance = Math.abs(target - maxOffset);

				Array.prototype.forEach.call(track.children, function(child) {
					const point = Math.min(maxOffset, child.offsetLeft - track.firstElementChild.offsetLeft);
					const distance = Math.abs(target - point);

					if (distance < bestDistance) {
						best = point;
						bestDistance = distance;
					}
				});

				track.classList.add('is-snapping');
				applyOffset(best);
			};

			const stopSnapping = function() {
				track.classList.remove('is-snapping');
			};

			track.addEventListener('transitionend', stopSnapping);

			const resetPointerState = function() {
				if (state.pointerId !== null && viewport.hasPointerCapture && viewport.hasPointerCapture(state.pointerId)) {
					viewport.releasePointerCapture(state.pointerId);
				}

				state.pointerId = null;
				state.axis = null;
			};

			const onWheel = function(e) {
				if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) {
					return;
				}

				const maxOffset = getMaxOffset();

				if (maxOffset <= 0) {
					return;
				}

				const nextOffset = state.offset + e.deltaX;

				if (nextOffset < 0 || nextOffset > maxOffset) {
					if ((state.offset === 0 && e.deltaX < 0) || (state.offset >= maxOffset && e.deltaX > 0)) {
						return;
					}
				}

				stopSnapping();
				applyOffset(nextOffset);
				e.preventDefault();

				clearTimeout(state.wheelTimer);
				state.wheelTimer = setTimeout(function() {
					snapTo(state.offset);
				}, 150);
			};

			const onPointerDown = function(e) {
				if (e.pointerType === 'mouse' && e.button !== 0) {
					return;
				}

				stopSnapping();
				state.pointerId = e.pointerId;
				state.startX = e.clientX;
				state.startY = e.clientY;
				state.lastX = e.clientX;
				state.lastTime = e.timeStamp;
				state.velocity = 0;
				state.offsetStart = state.offset;
				state.axis = null;
				state.dragged = false;
			};

			const onPointerMove = function(e) {
				if (state.pointerId !== e.pointerId) {
					return;
				}

				const dx = e.clientX - state.startX;
				const dy = e.clientY - state.startY;

				if (!state.axis) {
					if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
						return;
					}

					state.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';

					if (state.axis === 'x') {
						state.dragged = true;
						// Keep receiving moves when the pointer leaves the slider (mouse drag in preview)
						if (viewport.setPointerCapture) {
							try {
								viewport.setPointerCapture(e.pointerId);
							} catch (err) {}
						}
					}
				}

				if (state.axis !== 'x') {
					resetPointerState();
					return;
				}

				const dt = e.timeStamp - state.lastTime;

				if (dt > 0) {
					state.velocity = (e.clientX - state.lastX) / dt;
				}

				state.lastX = e.clientX;
				state.lastTime = e.timeStamp;

				applyOffset(state.offsetStart - dx);

				if (getMaxOffset() > 0) {
					e.preventDefault();
				}
			};

			const onPointerEnd = function(e) {
				if (state.pointerId !== e.pointerId) {
					return;
				}

				const wasDragging = state.axis === 'x';

				resetPointerState();

				if (wasDragging) {
					// Project the flick a bit forward so a short fast swipe moves to the next card
					const maxFling = viewport.clientWidth / 2;
					const fling = Math.max(-maxFling, Math.min(maxFling, state.velocity * 150));

					snapTo(state.offset - fling);
				}
			};

			// A drag must not turn into a click on whatever is under the pointer
			const onClickCapture = function(e) {
				if (state.dragged) {
					state.dragged = false;
					e.preventDefault();
					e.stopPropagation();
				}
			};

			const onDragStart = function(e) {
				e.preventDefault();
			};

			viewport.addEventListener('wheel', onWheel, { passive: false });
			viewport.addEventListener('pointerdown', onPointerDown);
			viewport.addEventListener('pointermove', onPointerMove);
			viewport.addEventListener('pointerup', onPointerEnd);
			viewport.addEventListener('pointercancel', onPointerEnd);
			viewport.addEventListener('click', onClickCapture, true);
			viewport.addEventListener('dragstart', onDragStart);

			$(viewport).data('appStoreSliderHandlers', {
				onWheel: onWheel,
				onPointerDown: onPointerDown,
				onPointerMove: onPointerMove,
				onPointerEnd: onPointerEnd,
				onClickCapture: onClickCapture,
				onDragStart: onDragStart,
				stopSnapping: stopSnapping,
				state: state,
				track: track
			});
		});
	}

	function unbindAppStoreSliders($block) {
		$block.find('[data-app-store-slider]').each(function() {
			const viewport = this;
			const handlers = $(viewport).data('appStoreSliderHandlers');

			if (handlers) {
				viewport.removeEventListener('wheel', handlers.onWheel);
				viewport.removeEventListener('pointerdown', handlers.onPointerDown);
				viewport.removeEventListener('pointermove', handlers.onPointerMove);
				viewport.removeEventListener('pointerup', handlers.onPointerEnd);
				viewport.removeEventListener('pointercancel', handlers.onPointerEnd);
				viewport.removeEventListener('click', handlers.onClickCapture, true);
				viewport.removeEventListener('dragstart', handlers.onDragStart);
				clearTimeout(handlers.state.wheelTimer);

				if (handlers.track) {
					handlers.track.removeEventListener('transitionend', handlers.stopSnapping);
					handlers.track.classList.remove('is-snapping');
					handlers.track.style.transform = '';
				}

				$(viewport).removeData('appStoreSliderHandlers');
			}

			delete viewport.dataset.appStoreSliderBound;
		});
	}

	function cleanupAppStorePageScroll($root) {
		const $scope = $root && $root.length ? $root : $('[data-app-store-page]');

		restorePortaledAppStoreStickies();

		$scope.each(function() {
			const $block = $(this);
			const namespace = $block.data('appStoreScrollNs');
			const stickyObserver = $block.data('appStoreStickyObserver');

			if (!namespace) {
				return;
			}

			if (stickyObserver && typeof stickyObserver.disconnect === 'function') {
				stickyObserver.disconnect();
			}

			$block.removeData('appStoreScrollNs appStoreScrollParent appStoreStickyObserver');
			$block.find('[data-app-store-page-sticky]').removeClass('is-visible').attr('aria-hidden', 'true');
			unbindAppStoreSliders($block);
		});
	}

	function initAppStorePage(screen) {
		cleanupAppStorePageScroll();

		$(screen).find('[data-app-store-page]').each(function() {
			const $block = $(this);
			const $sticky = $block.find('[data-app-store-page-sticky]');

			if (!$sticky.length || $block.data('appStoreScrollNs')) {
				return;
			}

			const namespace = 'appStorePage' + Date.now() + Math.floor(Math.random() * 1000);

			portalAppStoreSticky($sticky, $block);

			const stickyObserver = bindAppStoreStickyHeader($block, $sticky);

			$block.data('appStoreScrollNs', namespace);
			$block.data('appStoreStickyObserver', stickyObserver);
			bindAppStoreSliders($block);
		});
	}
	/**
	 * Initialize microphone recording blocks.
	 * @param {jQuery} screen - The screen element containing voice record blocks
	 * @param {Object} context - Context object with a ScreensManager instance
	 */
	// Active recorders must be torn down before the screen DOM is replaced — otherwise
	// Chrome keeps the tab mic indicator alive until max_duration fires on a dead MediaRecorder.
	const voiceRecordCleanups = [];

	function cleanupVoiceRecord() {
		while (voiceRecordCleanups.length) {
			const cleanup = voiceRecordCleanups.pop();
			try {
				cleanup();
			} catch (error) {
				console.error('[voice-record] Cleanup failed', error);
			}
		}
	}

	function initVoiceRecord(screen, context) {
		const screensManager = context && context.screensManager;

		$(screen).find('[data-voice-record]').each(function() {
			const $block = $(this);
			if ($block.data('voice-record-initialized')) {
				return;
			}
			$block.data('voice-record-initialized', true);

			const $recordButton = $block.find('[data-voice-record-button]');
			const $fileInput = $block.find('[data-voice-record-file]');
			const $retryButton = $block.find('[data-voice-record-retry]');
			const $status = $block.find('[data-voice-record-status]');
			const $timer = $block.find('[data-voice-record-timer]');
			const $processing = $block.find('[data-voice-record-processing]');
			const $result = $block.find('[data-voice-record-result]');
			const $audio = $block.find('[data-voice-record-audio]');
			const $value = $block.find('[data-voice-record-value]');
			const $wave = $block.find('[data-voice-record-wave]');
			const $peaks = $block.find('[data-voice-record-peaks]');
			const $play = $block.find('[data-voice-record-play]');
			const $current = $block.find('[data-voice-record-current]');
			const $duration = $block.find('[data-voice-record-duration]');
			const resizeNamespace = `resize.voice-record-${Date.now()}-${Math.random().toString(36).slice(2)}`;
			// Used in the editor to keep the player card visible across quiz_updated remounts.
			const previewStateKey = `w2w-voice-record-preview:${$value.attr('name') || `idx-${$block.index()}`}`;
			// Zero, negative and non-numeric limits are meaningless for a recording, so they
			// fall back to the block's default instead of silently disabling the cutoff.
			const configuredDuration = parseInt($block.attr('data-max-duration'), 10);
			const maxDuration = configuredDuration > 0 ? configuredDuration : 60;
			// Keeps the loader on screen for at least this long so the processing step stays
			// visible even when the upload answers instantly.
			const processingDuration = Math.max(0, parseFloat($block.attr('data-processing-duration')) || 0) * 1000;
			const uploadUrl = $block.attr('data-upload-url') || '/api/upload';
			// One bar is appended every WAVE_STEP_MS; the rest of the frames only redraw, so
			// the history scrolls at a readable pace instead of once per animation frame.
			const WAVE_STEP_MS = 90;
			const WAVE_BAR_WIDTH = 3;
			const WAVE_BAR_GAP = 3;
			// `/api/upload` rejects anything above 20 MB, so the block limit is capped there
			// even if a higher value somehow ends up in the quiz JSON.
			const SERVER_MAX_UPLOAD_MB = 20;
			const configuredMaxMb = parseFloat($block.attr('data-max-file-size-mb'));
			const maxFileSizeMb = configuredMaxMb > 0
				? Math.min(configuredMaxMb, SERVER_MAX_UPLOAD_MB)
				: SERVER_MAX_UPLOAD_MB;
			const maxUploadBytes = maxFileSizeMb * 1024 * 1024;
			const PEAK_BUCKETS = 200;
			let mediaRecorder = null;
			let mediaStream = null;
			let chunks = [];
			let timerInterval = null;
			let processingTimeout = null;
			let elapsedSeconds = 0;
			let previewUrl = null;
			let audioContext = null;
			let analyser = null;
			let analyserBuffer = null;
			let waveFrame = null;
			let waveLevels = [];
			let waveLastPush = 0;
			// waveLevels scrolls and drops old bars; this keeps the full envelope so the
			// player can draw the whole recording afterwards.
			let recordedLevels = [];
			let destroyed = false;
			let onRecorderStop = null;

			function isEditMode() {
				return typeof getParams === 'function'
					&& (String(getParams('edit_mode')) === '1' || getParams('edit_mode') === true);
			}

			function rememberPreviewState(state) {
				if (!isEditMode()) {
					return;
				}
				try {
					sessionStorage.setItem(previewStateKey, state);
				} catch (error) {
					console.error('[voice-record] Could not persist preview state', error);
				}
			}

			function text(name, fallback) {
				return $block.attr(`data-${name}-text`) || fallback;
			}

			function formatTime(seconds) {
				const minutes = Math.floor(seconds / 60);
				const remainder = seconds % 60;
				return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
			}

			function validate() {
				if (screensManager && typeof screensManager.validateAllRequirements === 'function') {
					screensManager.validateAllRequirements(screen);
				}
			}

			/**
			 * Same wiring as the file-upload block: the callback string is stored as JSON in
			 * `data-callback` and receives `{ url, file }` after a successful upload.
			 */
			function runCallback(responseUrl, file) {
				const rawData = $block.attr('data-callback') || '';
				if (!rawData) {
					return;
				}

				try {
					const data = JSON.parse(rawData.replace(/&quot;/g, '"'));
					const callback = data && data.callback;
					if (!callback) {
						return;
					}

					if (typeof evaluateFunction !== 'function') {
						console.error('[voice-record] evaluateFunction is not available');
						return;
					}

					evaluateFunction(callback, { url: responseUrl, file: file });
				} catch (error) {
					console.error('[voice-record] Error executing callback', error);
				}
			}

			function showOversized() {
				console.error('[voice-record] File exceeds the allowed size', `${maxFileSizeMb} MB`);
				$status.addClass('is-error').text(text('oversized', 'File is too large. Please choose a smaller file.'));
				$recordButton.prop('disabled', false);
			}

			/**
			 * Required + denied mic + no upload leaves the user stuck unless we say why
			 * Continue is blocked. The button stays enabled so they can retry the prompt.
			 */
			function showMicDenied(error) {
				console.error('[voice-record] getUserMedia rejected', error);
				clearTimers();
				stopWave();
				stopStream();
				$block.removeClass('is-recording is-processing');
				$processing.prop('hidden', true);
				$recordButton.prop('hidden', false).prop('disabled', false);
				$status.addClass('is-error').text(text(
					'mic-denied',
					'Microphone access is required. Please allow it and try again.'
				));
				validate();
			}

			function stopStream() {
				if (mediaStream) {
					mediaStream.getTracks().forEach(function(track) {
						track.stop();
					});
					mediaStream = null;
				}
			}

			function clearTimers() {
				if (timerInterval) {
					clearInterval(timerInterval);
					timerInterval = null;
				}
				if (processingTimeout) {
					clearTimeout(processingTimeout);
					processingTimeout = null;
				}
			}

			/**
			 * Tear the instance down before the screen DOM is wiped. Mid-recording must not
			 * call finishRecording (that would upload a partial take after the user left).
			 */
			function destroy() {
				if (destroyed) {
					return;
				}
				destroyed = true;

				clearTimers();
				stopWave();
				clearPreviewMedia();

				if (mediaRecorder) {
					if (onRecorderStop) {
						mediaRecorder.removeEventListener('stop', onRecorderStop);
						onRecorderStop = null;
					}
					if (mediaRecorder.state !== 'inactive') {
						try {
							mediaRecorder.stop();
						} catch (error) {
							console.error('[voice-record] Could not stop MediaRecorder', error);
						}
					}
					mediaRecorder = null;
				}

				stopStream();

				$(window).off(resizeNamespace);
				$recordButton.off('.voice-record');
				$fileInput.off('.voice-record');
				$retryButton.off('.voice-record');
				$play.off('.voice-record');
				$audio.off('.voice-record');
			}

			voiceRecordCleanups.push(destroy);

			/**
			 * Size the backing store to the CSS box so bars stay sharp on retina screens.
			 * Returns the drawing context in CSS pixels, or null when there is nothing to draw on.
			 */
			function prepareCanvas($canvas) {
				const canvas = $canvas.get(0);
				if (!canvas) {
					return null;
				}

				const ratio = window.devicePixelRatio || 1;
				const width = canvas.clientWidth;
				const height = canvas.clientHeight;
				if (!width || !height) {
					return null;
				}

				if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
					canvas.width = Math.round(width * ratio);
					canvas.height = Math.round(height * ratio);
				}

				const ctx = canvas.getContext('2d');
				ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
				ctx.clearRect(0, 0, width, height);

				return { ctx: ctx, width: width, height: height };
			}

			function barCapacity(width) {
				return Math.max(1, Math.floor(width / (WAVE_BAR_WIDTH + WAVE_BAR_GAP)));
			}

			/**
			 * Squeeze an arbitrary number of levels into `count` bars, keeping the loudest
			 * sample of each bucket so short peaks stay visible.
			 */
			function resampleLevels(levels, count) {
				if (!levels.length || count <= 0) {
					return [];
				}

				const bars = [];
				for (let i = 0; i < count; i += 1) {
					const start = Math.floor((i * levels.length) / count);
					const end = Math.max(start + 1, Math.floor(((i + 1) * levels.length) / count));
					let peak = 0;
					for (let j = start; j < end && j < levels.length; j += 1) {
						peak = Math.max(peak, levels[j]);
					}
					bars.push(peak);
				}

				return bars;
			}

			/**
			 * Paint centered rounded bars. Bars past `playedRatio` are dimmed, which the
			 * player uses to show playback progress; the live meter passes 1 for all-solid.
			 */
			function drawBars($canvas, levels, playedRatio) {
				const surface = prepareCanvas($canvas);
				if (!surface) {
					return;
				}

				const step = WAVE_BAR_WIDTH + WAVE_BAR_GAP;
				const centerY = surface.height / 2;
				const playedBars = levels.length * playedRatio;
				// Keep a short history centered instead of clinging to the left edge, so the
				// live meter grows outwards from the middle like the design.
				const offsetX = Math.max(0, (surface.width - (levels.length * step)) / 2);
				surface.ctx.fillStyle = window.getComputedStyle($canvas.get(0)).color;

				levels.forEach(function(level, index) {
					const barHeight = Math.max(WAVE_BAR_WIDTH, level * surface.height);
					const x = offsetX + (index * step);
					const y = centerY - (barHeight / 2);

					surface.ctx.globalAlpha = index < playedBars ? 1 : 0.5;
					surface.ctx.beginPath();
					if (typeof surface.ctx.roundRect === 'function') {
						surface.ctx.roundRect(x, y, WAVE_BAR_WIDTH, barHeight, WAVE_BAR_WIDTH / 2);
					} else {
						surface.ctx.rect(x, y, WAVE_BAR_WIDTH, barHeight);
					}
					surface.ctx.fill();
				});

				surface.ctx.globalAlpha = 1;
			}

			function drawWave() {
				const canvas = $wave.get(0);
				if (canvas && canvas.clientWidth) {
					const capacity = barCapacity(canvas.clientWidth);
					if (waveLevels.length > capacity) {
						waveLevels = waveLevels.slice(-capacity);
					}
				}

				drawBars($wave, waveLevels, 1);
			}

			function playedRatio() {
				const audio = $audio.get(0);
				const total = playbackDuration();
				if (!audio || !total) {
					return 0;
				}

				return Math.min(1, audio.currentTime / total);
			}

			function drawPeaks() {
				const canvas = $peaks.get(0);
				if (!canvas || !canvas.clientWidth) {
					return;
				}

				drawBars($peaks, resampleLevels(recordedLevels, barCapacity(canvas.clientWidth)), playedRatio());
			}

			/**
			 * Chrome reports `Infinity` for MediaRecorder WebM until it is fully seeked, so the
			 * measured recording length is the reliable source for the player's total time.
			 */
			function playbackDuration() {
				const audio = $audio.get(0);
				const nativeDuration = audio ? audio.duration : 0;

				return Number.isFinite(nativeDuration) && nativeDuration > 0 ? nativeDuration : elapsedSeconds;
			}

			function stopWave() {
				if (waveFrame) {
					window.cancelAnimationFrame(waveFrame);
					waveFrame = null;
				}
				if (audioContext) {
					audioContext.close().catch(function(error) {
						console.error('[voice-record] Could not close AudioContext', error);
					});
					audioContext = null;
				}
				analyser = null;
				analyserBuffer = null;
				waveLevels = [];
				waveLastPush = 0;
				$wave.prop('hidden', true);
			}

			/**
			 * Live loudness meter for the incoming microphone stream. Failing to build the
			 * audio graph must never block recording, so any error just leaves it hidden.
			 */
			function startWave(stream) {
				const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
				if (!$wave.length || !AudioContextCtor) {
					return;
				}

				try {
					audioContext = new AudioContextCtor();
					if (audioContext.state === 'suspended') {
						audioContext.resume().catch(function(error) {
							console.error('[voice-record] Could not resume AudioContext', error);
						});
					}

					analyser = audioContext.createAnalyser();
					analyser.fftSize = 1024;
					analyser.smoothingTimeConstant = 0.7;
					audioContext.createMediaStreamSource(stream).connect(analyser);
					analyserBuffer = new Uint8Array(analyser.fftSize);
				} catch (e) {
					stopWave();
					return;
				}

				waveLevels = [];
				waveLastPush = 0;
				recordedLevels = [];
				$wave.prop('hidden', false);

				function frame(timestamp) {
					if (!analyser) {
						return;
					}

					if (!waveLastPush || timestamp - waveLastPush >= WAVE_STEP_MS) {
						waveLastPush = timestamp;
						analyser.getByteTimeDomainData(analyserBuffer);

						let sumOfSquares = 0;
						for (let i = 0; i < analyserBuffer.length; i += 1) {
							const deviation = (analyserBuffer[i] - 128) / 128;
							sumOfSquares += deviation * deviation;
						}

						// RMS of a normal speaking voice sits well below 1, so amplify it before
						// clamping — otherwise every bar would render as a flat minimum-height line.
						const rms = Math.sqrt(sumOfSquares / analyserBuffer.length);
						const level = Math.min(1, rms * 3.5);
						waveLevels.push(level);
						recordedLevels.push(level);
					}

					drawWave();
					waveFrame = window.requestAnimationFrame(frame);
				}

				waveFrame = window.requestAnimationFrame(frame);
			}

			function stopPlayback() {
				const audio = $audio.get(0);
				if (audio) {
					audio.pause();
					try {
						audio.currentTime = 0;
					} catch (error) {
						// Seeking can throw if the media resource was already cleared.
						console.error('[voice-record] Could not reset playback position', error);
					}
				}
				$block.removeClass('is-playing');
			}

			/**
			 * Drop the <audio> resource before revoking the blob. Leaving src=blob:… after
			 * revokeObjectURL makes the browser keep refetching and spam ERR_FILE_NOT_FOUND
			 * once the screen is left (Continue / remount).
			 */
			function clearPreviewMedia() {
				const audio = $audio.get(0);
				if (audio) {
					audio.pause();
					audio.removeAttribute('src');
					try {
						audio.load();
					} catch (error) {
						console.error('[voice-record] Could not reset audio element', error);
					}
				} else {
					$audio.removeAttr('src');
				}
				$block.removeClass('is-playing');
				if (previewUrl) {
					URL.revokeObjectURL(previewUrl);
					previewUrl = null;
				}
			}

			/**
			 * Prefer the uploaded CDN URL for playback so we do not keep a temporary blob
			 * alive after the answer is already persisted.
			 */
			function adoptRemotePreview(url) {
				if (!url) {
					return;
				}
				stopPlayback();
				if (previewUrl) {
					URL.revokeObjectURL(previewUrl);
					previewUrl = null;
				}
				$audio.attr('src', url);
			}

			function showIdle() {
				clearTimers();
				stopWave();
				stopPlayback();
				$block.removeClass('is-recording is-processing is-complete');
				$status.removeClass('is-error').text(text('idle', 'Tap to start recording'));
				$timer.prop('hidden', false).text('00:00');
				$current.text('00:00');
				$duration.text('00:00');
				$recordButton.prop('hidden', false).prop('disabled', false);
				$processing.prop('hidden', true);
				$result.prop('hidden', true);
				rememberPreviewState('idle');
			}

			/**
			 * Failures (denied permission, rejected upload, unsupported codec) are a developer
			 * concern, so they only reach the console. A finished take still gets its player
			 * card; without one the block simply returns to its idle state.
			 */
			function showError(detail, cause) {
				if (detail) {
					console.error('[voice-record]', detail, cause === undefined ? '' : cause);
				}

				clearTimers();
				stopWave();
				stopStream();
				$processing.prop('hidden', true);

				if (previewUrl) {
					$block.removeClass('is-recording is-processing').addClass('is-complete');
					$status.removeClass('is-error').text(text('recorded', 'Voice captured'));
					$recordButton.prop('hidden', true);
					showPlayer();
					rememberPreviewState('complete');
					validate();
					return;
				}

				showIdle();
				validate();
			}

			function getMimeType() {
				if (!window.MediaRecorder || typeof window.MediaRecorder.isTypeSupported !== 'function') {
					return '';
				}

				const types = [
					'audio/webm;codecs=opus',
					'audio/ogg;codecs=opus',
					'audio/mp4'
				];

				return types.find(function(type) {
					return window.MediaRecorder.isTypeSupported(type);
				}) || '';
			}

			function getExtension(mimeType) {
				if (mimeType.indexOf('mp4') !== -1) {
					return 'm4a';
				}
				if (mimeType.indexOf('ogg') !== -1) {
					return 'ogg';
				}
				return 'webm';
			}

			/**
			 * Reveal the player card and draw the recording envelope. The canvas has no size
			 * while the card is hidden, so the first draw has to happen after unhiding it.
			 */
			function showPlayer() {
				$result.prop('hidden', false);
				// The card carries its own timings, so the live counter has nothing left to say.
				$timer.prop('hidden', true);
				$current.text('00:00');
				$duration.text(formatTime(Math.round(playbackDuration())));
				drawPeaks();
			}

			function uploadRecording(blob, uploadName) {
				if (blob && blob.size > maxUploadBytes) {
					showIdle();
					showOversized();
					return;
				}

				stopWave();
				$block.removeClass('is-recording is-complete').addClass('is-processing');
				$status.removeClass('is-error').text(text('processing', 'Processing...'));
				$recordButton.prop('hidden', true);
				$processing.prop('hidden', false);
				$result.prop('hidden', true);

				const processingStartedAt = Date.now();

				/** Hold the loader for the configured minimum before revealing the outcome. */
				function whenProcessingDone(callback) {
					const remaining = processingDuration - (Date.now() - processingStartedAt);
					if (remaining <= 0) {
						callback();
						return;
					}
					processingTimeout = setTimeout(callback, remaining);
				}

				const mimeType = blob.type || 'audio/webm';
				const filename = uploadName || `voice-record-${Date.now()}.${getExtension(mimeType)}`;
				const formData = new FormData();
				formData.append('file', blob, filename);

				$.ajax({
					url: uploadUrl,
					type: 'POST',
					data: formData,
					processData: false,
					contentType: false,
					headers: {
						'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
					},
					success: function(response) {
						if (!response || !response.url) {
							whenProcessingDone(function() {
								showError('Upload response has no url', response);
							});
							return;
						}

						$value.val(response.url).trigger('change');
						w2w.setUserProperty($value.attr('name'), response.url);
						w2w.logEvent(`Answer ${$value.attr('name')}`, { value: response.url });
						runCallback(response.url, blob);
						adoptRemotePreview(response.url);

						whenProcessingDone(function() {
							$block.removeClass('is-processing').addClass('is-complete');
							$status.removeClass('is-error').text(text('recorded', 'Voice captured'));
							$processing.prop('hidden', true);
							showPlayer();
							rememberPreviewState('complete');
							validate();
						});
					},
					error: function(xhr) {
						const responseMessage = (xhr.responseJSON && xhr.responseJSON.message) || xhr.statusText;
						whenProcessingDone(function() {
							showError(`Upload failed (${xhr.status})`, responseMessage);
						});
					}
				});
			}

			/**
			 * Uploaded files carry no live meter history, so the envelope is read back from the
			 * decoded samples. Formats the browser cannot decode (some .mov files) resolve to
			 * null and the card falls back to a flat line.
			 */
			function readPeaks(file) {
				const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
				if (!AudioContextCtor || typeof file.arrayBuffer !== 'function') {
					return Promise.resolve(null);
				}

				const context = new AudioContextCtor();

				return file.arrayBuffer()
					.then(function(data) {
						return context.decodeAudioData(data);
					})
					.then(function(buffer) {
						const samples = buffer.getChannelData(0);
						const bucketSize = Math.floor(samples.length / PEAK_BUCKETS) || 1;
						const levels = [];

						for (let bucket = 0; bucket < PEAK_BUCKETS; bucket += 1) {
							const start = bucket * bucketSize;
							const end = Math.min(start + bucketSize, samples.length);
							let peak = 0;
							for (let i = start; i < end; i += 1) {
								peak = Math.max(peak, Math.abs(samples[i]));
							}
							levels.push(Math.min(1, peak));
						}

						return { levels: levels, duration: buffer.duration };
					})
					.catch(function(error) {
						console.error('[voice-record] Could not decode the uploaded file', error);
						return null;
					})
					.finally(function() {
						Promise.resolve(context.close()).catch(function(error) {
							console.error('[voice-record] Could not close the audio context', error);
						});
					});
			}

			function useUploadedFile(file) {
				if (file.size > maxUploadBytes) {
					showOversized();
					return;
				}

				clearTimers();
				stopPlayback();
				recordedLevels = [];
				elapsedSeconds = 0;

				if (previewUrl) {
					URL.revokeObjectURL(previewUrl);
				}
				previewUrl = URL.createObjectURL(file);
				$audio.attr('src', previewUrl);

				// Decoding runs alongside the upload; if it wins the race the card is redrawn.
				readPeaks(file).then(function(peaks) {
					if (!peaks) {
						return;
					}
					recordedLevels = peaks.levels;
					elapsedSeconds = Math.round(peaks.duration);
					if ($block.hasClass('is-complete')) {
						showPlayer();
					}
				});

				uploadRecording(file, file.name);
			}

			function finishRecording() {
				if (destroyed) {
					return;
				}
				clearTimers();
				stopWave();
				stopStream();

				if (!chunks.length) {
					showError('Recording produced no audio chunks');
					return;
				}

				const blob = new Blob(chunks, {
					type: mediaRecorder && mediaRecorder.mimeType ? mediaRecorder.mimeType : 'audio/webm'
				});

				if (previewUrl) {
					URL.revokeObjectURL(previewUrl);
				}
				previewUrl = URL.createObjectURL(blob);
				$audio.attr('src', previewUrl);
				uploadRecording(blob);
			}

			function stopRecording() {
				if (!mediaRecorder || mediaRecorder.state === 'inactive') {
					return;
				}
				clearTimers();
				mediaRecorder.stop();
				stopStream();
			}

			function startTimer() {
				elapsedSeconds = 0;
				$timer.text(formatTime(elapsedSeconds));
				timerInterval = setInterval(function() {
					elapsedSeconds += 1;
					$timer.text(formatTime(elapsedSeconds));
					if (elapsedSeconds >= maxDuration) {
						stopRecording();
					}
				}, 1000);
			}

			function startRecording() {
				if (destroyed) {
					return;
				}
				if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || !window.MediaRecorder) {
					showError('MediaRecorder or getUserMedia is not available in this browser');
					return;
				}

				$recordButton.prop('disabled', true);
				navigator.mediaDevices.getUserMedia({ audio: true })
					.then(function(stream) {
						mediaStream = stream;
						chunks = [];
						const mimeType = getMimeType();
						mediaRecorder = mimeType
							? new window.MediaRecorder(stream, { mimeType: mimeType })
							: new window.MediaRecorder(stream);

						mediaRecorder.addEventListener('dataavailable', function(event) {
							if (event.data && event.data.size > 0) {
								chunks.push(event.data);
							}
						});
						onRecorderStop = function() {
							onRecorderStop = null;
							finishRecording();
						};
						mediaRecorder.addEventListener('stop', onRecorderStop, { once: true });
						mediaRecorder.addEventListener('error', function(event) {
							showError('MediaRecorder error', event && event.error);
						}, { once: true });
						mediaRecorder.start();

						$block.removeClass('is-processing is-complete').addClass('is-recording');
						$status.removeClass('is-error').text(text('recording', 'Tap to stop recording'));
						$recordButton.prop('disabled', false);
						$processing.prop('hidden', true);
						$result.prop('hidden', true);
						startWave(stream);
						startTimer();
					})
					.catch(function(error) {
						showMicDenied(error);
					});
			}

			function buildDemoPeaks() {
				const levels = [];
				for (let i = 0; i < PEAK_BUCKETS; i += 1) {
					levels.push(0.2 + 0.6 * Math.abs(Math.sin(i / 6)) * (0.35 + 0.65 * Math.abs(Math.sin(i / 2.5))));
				}
				return levels;
			}

			/**
			 * Editor remounts wipe the real recording, but designers still need the player card
			 * on screen while tweaking recorded_title / player theme vars. A synthetic envelope
			 * is enough to style against; playback stays empty until they record again.
			 */
			function showEditorCompletePreview() {
				recordedLevels = buildDemoPeaks();
				elapsedSeconds = 5;
				$block.removeClass('is-recording is-processing').addClass('is-complete');
				$status.removeClass('is-error').text(text('recorded', 'Voice captured'));
				$recordButton.prop('hidden', true);
				$processing.prop('hidden', true);
				$timer.prop('hidden', true);
				showPlayer();
			}

			$recordButton.on('click.voice-record', function() {
				if (destroyed) {
					return;
				}
				if (mediaRecorder && mediaRecorder.state === 'recording') {
					stopRecording();
				} else {
					startRecording();
				}
			});

			$fileInput.on('change.voice-record', function() {
				if (destroyed) {
					return;
				}
				const file = this.files && this.files[0];
				// Reset first so picking the same file again still fires a change event.
				$fileInput.val('');
				if (file) {
					useUploadedFile(file);
				}
			});

			$play.on('click.voice-record', function() {
				const audio = $audio.get(0);
				if (!audio || !audio.src) {
					return;
				}

				if (audio.paused) {
					audio.play().catch(function(error) {
						console.error('[voice-record] Playback failed', error);
					});
				} else {
					audio.pause();
				}
			});

			$audio
				.on("play.voice-record", function () {
					$block.addClass("is-playing");
				})
				.on("pause.voice-record", function () {
					$block.removeClass("is-playing");
				})
				.on("timeupdate.voice-record", function () {
					const played = Math.min(playbackDuration(), $audio.get(0).currentTime);
					$current.text(formatTime(Math.floor(played)));
					drawPeaks();
				})
				.on("ended.voice-record", function () {
					stopPlayback();
					$current.text("00:00");
					drawPeaks();
				});

			$(window).on(resizeNamespace, function() {
				if ($block.hasClass('is-complete')) {
					drawPeaks();
				} else if ($block.hasClass('is-recording')) {
					drawWave();
				}
			});

			$retryButton.on("click.voice-record", function () {
				clearPreviewMedia();
				recordedLevels = [];
				$value.val("");
				w2w.setUserProperty($value.attr("name"), "");
				showIdle();
				validate();
			});

			showIdle();

			if (isEditMode()) {
				try {
					if (sessionStorage.getItem(previewStateKey) === 'complete') {
						showEditorCompletePreview();
					}
				} catch (error) {
					console.error('[voice-record] Could not restore preview state', error);
				}
			}
		});
	}

	window.BlockInteractions = {
		initSpinWheel: initSpinWheel,
		initScratchDiscount: initScratchDiscount,
		initSignature: initSignature,
		initVoiceRecord: initVoiceRecord,
		cleanupVoiceRecord: cleanupVoiceRecord,
		initLiking: initLiking,
		initToggle: initToggle,
		initPromoTicketCodes: initPromoTicketCodes,
		initAppStorePage: initAppStorePage,
		appStorePageView: appStorePageView,
		resetFullscreenVideo: resetFullscreenVideo,
		initFullscreenVideo: initFullscreenVideo,
		initVideoControls: initVideoControls,
		cleanupVideoControls: cleanupVideoControls
	};
})();

